// Landlord Bank Service - API integration for bank account management

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Ensure API_BASE_URL ends with /api
const BASE_URL = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

// Types
export interface BankCode {
  name: string;
  code: string;
}

export interface BankAccount {
  id: string;
  bankName: string;
  bankCode: string;
  accountNumber: string; // Masked for security
  accountHolderName: string;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BankAccountData {
  bankName: string;
  bankCode: string;
  accountNumber: string;
  accountHolderName: string;
  isPrimary?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  count?: number;
}

export interface BankCodesResponse extends ApiResponse<BankCode[]> {
  count: number;
}

export interface BankAccountsResponse extends ApiResponse<BankAccount[]> {
  count: number;
}

export interface BankAccountResponse extends ApiResponse<BankAccount> {
  details?: string[];
  warnings?: string[];
}

export interface ValidationResponse extends ApiResponse<{
  isValid: boolean;
  bankName: string;
  bankCode: string;
  accountNumber: string;
  validatedAt: string;
  message: string;
}> { }

class LandlordBankService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const { authTokenService } = await import('./auth.service');
      const authHeader = authTokenService.getAuthHeader();
      const url = `${BASE_URL}${endpoint}`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...options.headers as any,
      };
      if (authHeader) headers['Authorization'] = authHeader;

      const response = await fetch(url, {
        headers,
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  }

  /**
   * Get all supported bank codes
   */
  async getBankCodes(): Promise<BankCodesResponse> {
    return this.makeRequest<BankCodesResponse>('/organization/bank-codes');
  }

  /**
   * Get bank accounts for the active organization (landlordId ignored — org context).
   */
  async getBankAccounts(_landlordId?: string): Promise<BankAccountsResponse> {
    return this.makeRequest<BankAccountsResponse>(`/organization/bank-accounts`);
  }

  /**
   * Add a bank account for the active organization.
   */
  async addBankAccount(
    _landlordId: string | undefined,
    bankData: BankAccountData
  ): Promise<BankAccountResponse> {
    return this.makeRequest<BankAccountResponse>(`/organization/bank-accounts`, {
      method: 'POST',
      body: JSON.stringify(bankData),
    });
  }

  /**
   * Update an existing bank account
   */
  async updateBankAccount(
    _landlordId: string | undefined,
    accountId: string,
    bankData: Partial<BankAccountData>
  ): Promise<BankAccountResponse> {
    return this.makeRequest<BankAccountResponse>(
      `/organization/bank-accounts/${accountId}`,
      {
        method: 'PUT',
        body: JSON.stringify(bankData),
      }
    );
  }

  /**
   * Delete a bank account
   */
  async deleteBankAccount(
    _landlordId: string | undefined,
    accountId: string
  ): Promise<ApiResponse<void>> {
    return this.makeRequest<ApiResponse<void>>(
      `/organization/bank-accounts/${accountId}`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * Validate a bank account
   */
  async validateBankAccount(
    _landlordId: string | undefined,
    accountId: string
  ): Promise<ValidationResponse> {
    return this.makeRequest<ValidationResponse>(
      `/organization/bank-accounts/${accountId}/validate`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Utility method to validate bank account data before submission
   */
  validateBankAccountData(data: BankAccountData): string[] {
    const errors: string[] = [];

    // Bank name validation
    if (!data.bankName?.trim()) {
      errors.push('Bank name is required');
    } else if (data.bankName.trim().length < 2) {
      errors.push('Bank name must be at least 2 characters');
    } else if (data.bankName.trim().length > 100) {
      errors.push('Bank name cannot exceed 100 characters');
    }

    // Bank code validation
    if (!data.bankCode?.trim()) {
      errors.push('Bank code is required');
    } else if (!/^[0-9]+$/.test(data.bankCode.trim()) && !['MPESA', 'AIRTEL', 'TKASH'].includes(data.bankCode.trim())) {
      errors.push('Invalid bank code format');
    }

    // Account number validation
    if (!data.accountNumber?.trim()) {
      errors.push('Account number is required');
    } else {
      const cleanNumber = data.accountNumber.trim().replace(/[\s-]/g, '');

      if (!/^[0-9]+$/.test(cleanNumber)) {
        errors.push('Account number must contain only digits');
      } else if (cleanNumber.length < 8) {
        errors.push('Account number must be at least 8 digits');
      } else if (cleanNumber.length > 20) {
        errors.push('Account number cannot exceed 20 digits');
      }

      // Mobile money validation
      if (['MPESA', 'AIRTEL', 'TKASH'].includes(data.bankCode?.trim())) {
        if (!cleanNumber.startsWith('254')) {
          errors.push('Mobile money numbers must start with 254');
        } else if (cleanNumber.length !== 12) {
          errors.push('Mobile money numbers must be exactly 12 digits');
        }
      }
    }

    // Account holder name validation
    if (!data.accountHolderName?.trim()) {
      errors.push('Account holder name is required');
    } else {
      const trimmedName = data.accountHolderName.trim();

      if (trimmedName.length < 2) {
        errors.push('Account holder name must be at least 2 characters');
      } else if (trimmedName.length > 100) {
        errors.push('Account holder name cannot exceed 100 characters');
      } else if (!/^[a-zA-Z\s\-']+$/.test(trimmedName)) {
        errors.push('Account holder name can only contain letters, spaces, hyphens, and apostrophes');
      } else if (/^\d+$/.test(trimmedName)) {
        errors.push('Account holder name cannot be only numbers');
      }
    }

    return errors;
  }

  /**
   * Utility method to format bank account for display
   */
  formatBankAccountForDisplay(account: BankAccount): string {
    return `${account.bankName} ••••${account.accountNumber.slice(-4)}`;
  }

  /**
   * Utility method to get bank name from code
   */
  async getBankNameFromCode(bankCode: string): Promise<string> {
    try {
      const response = await this.getBankCodes();
      if (response.success && response.data) {
        const bank = response.data.find(b => b.code === bankCode);
        return bank?.name || 'Unknown Bank';
      }
      return 'Unknown Bank';
    } catch (error) {
      console.error('Error fetching bank name:', error);
      return 'Unknown Bank';
    }
  }
}

// Export singleton instance
export const landlordBankService = new LandlordBankService();

// Types are already exported above, no need to re-export
